import React from 'react'
import axios from 'axios'
import DOMPurify from 'dompurify'
import { pastDueOf, currencyFormat, healthOf, autoNote, ptpTags, daysAgo } from './arAgingModel'

const HEALTH_TONE_LABEL = { bad: 'ar-pill-danger', warn: 'ar-pill-warn', primary: 'ar-pill-primary' }

// Activity timeline dot color by subtype (design map).
const ACTIVITY_TONE = {
  call: 'info', email: 'primary', sms: 'purple', note: 'ink-4',
  escalation: 'danger', 'pause-intent': 'warn', pause: 'warn', payment: 'ok', ptp: 'info'
}

const initials = (name) => String(name || '?').trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase()

const formatWhen = (value) => {
  const days = daysAgo(value)
  if (days === 0) return 'TODAY'
  if (days === 1) return 'YESTERDAY'
  if (days == null) return ''
  const date = new Date(value)
  return `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(2)}`
}

// Sticky right panel for the selected company. Loads its collections activity
// timeline; every quick action is supplied by the container (shared action set).
const ArDetailPanel = ({ company, url, ptp, excluded, actions, onLogNote, activityVersion }) => {
  const [activities, setActivities] = React.useState(null)
  const companyId = company?.companyId ?? null
  const endpoint = `${url}/api/aspire/ar-aging/collections`

  React.useEffect(() => {
    let cancelled = false
    setActivities(null)
    if (!company) return undefined
    if (companyId === null) { setActivities([]); return undefined }
    axios.get(`${endpoint}/activities`, { params: { companyId } })
      .then(response => { if (!cancelled) setActivities(response.data) })
      .catch(() => { if (!cancelled) setActivities([]) })
    return () => { cancelled = true }
  }, [company, companyId, endpoint, activityVersion])

  if (!company) {
    return <aside className="ar-panel ar-panel-empty">Select a company to see collections detail.</aside>
  }

  const buckets = company.buckets
  const health = healthOf(company, buckets, ptp)
  const tags = ptpTags(company, ptp, daysAgo(company.lastActivityAt))
  const runNote = autoNote(company, buckets, { excluded })
  const properties = [...(company.properties || [])].sort((a, b) => pastDueOf(b.buckets) - pastDueOf(a.buckets))
  const lastPaid = company.paymentStats?.lastPaymentDate

  return (
    <aside className="ar-panel">
      <div className="ar-panel-head">
        <h2 className="ar-panel-name">{company.companyName}</h2>
        <span className={`ar-pill ${HEALTH_TONE_LABEL[health.tone]}`}>{health.label}</span>
      </div>
      <div className="ar-panel-runnote">
        {runNote}
        {tags.map((tag, index) => <span key={index} className={`ar-row-tag tone-${tag.tone}`}>{tag.text}</span>)}
      </div>

      {company.pauseRequested && (
        <div className="ar-panel-paused">
          <span className="ar-paused-text">Maintenance pause requested</span>
        </div>
      )}

      <div className="ar-panel-stats">
        <div className="ar-stat-cell">
          <span className="ar-stat-key">PAST DUE</span>
          <strong className="ar-stat-val risk">{currencyFormat(pastDueOf(buckets))}</strong>
        </div>
        <div className="ar-stat-cell">
          <span className="ar-stat-key">AVG TO PAY</span>
          <strong className="ar-stat-val">{Number.isFinite(company.paymentStats?.avgDaysToPay) ? `${company.paymentStats.avgDaysToPay}d` : '—'}</strong>
        </div>
        <div className="ar-stat-cell">
          <span className="ar-stat-key">LAST PAID</span>
          <strong className="ar-stat-val">
            {lastPaid
              ? <>{currencyFormat(company.paymentStats.lastPaymentAmount)}<span className="ar-stat-sub">{formatWhen(lastPaid)} · {daysAgo(lastPaid)}d ago</span></>
              : 'Never'}
          </strong>
        </div>
      </div>

      <div className="ar-panel-section">
        <div className="ar-panel-label">BILLING CONTACTS</div>
        {(company.billingContacts || []).length === 0 && <div className="ar-panel-empty-line">None on file</div>}
        {(company.billingContacts || []).map(contact => (
          <div key={contact.contactId || contact.name} className="ar-contact">
            <span className="ar-avatar">{initials(contact.name)}</span>
            <div className="ar-contact-body">
              <div className="ar-contact-name">{contact.name}</div>
              <div className="ar-contact-line">{[contact.email, contact.phone].filter(Boolean).join(' · ') || '—'}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="ar-panel-chips">
        {actions.map((action, index) => (
          <button
            key={index}
            type="button"
            className={`ar-chip ${action.tone ? `tone-${action.tone}` : ''}`}
            disabled={action.disabled}
            onClick={() => action.run(company)}
          >{action.label}</button>
        ))}
      </div>

      <div className="ar-panel-section">
        <div className="ar-panel-label">PROPERTIES &amp; OPEN ITEMS</div>
        {properties.map(property => {
          const entries = [
            ...(property.detailEntries || []),
            ...(property.opportunities || []).flatMap(opp => opp.invoices || [])
          ]
          return (
            <div key={property.propertyId} className="ar-prop-group">
              <div className="ar-prop-head">
                <span className="ar-prop-name">{property.propertyName}</span>
                <span className="ar-prop-id">#{property.propertyId}</span>
                <span className="ar-prop-bal">{currencyFormat(pastDueOf(property.buckets))}</span>
              </div>
              {entries.length === 0 && <div className="ar-prop-empty">No open-item detail</div>}
              {entries.map((entry, index) => {
                const balance = entry.amount || 0
                const overdue = entry.age > 0
                return (
                  <div key={`${entry.arNumber || entry.arType}-${index}`} className="ar-inv-row">
                    <div className="ar-inv-main">
                      <span className="ar-inv-num">{entry.arNumber || entry.arType || 'AR item'}</span>
                      <div className="ar-inv-meta">
                        {entry.arType && entry.arType !== 'Invoice' ? `${entry.arType.toUpperCase()} · ` : ''}
                        {entry.arDate ? `INV ${formatWhen(entry.arDate)}` : ''}
                        {entry.age > 0 ? ` · ${entry.age}D PAST DUE` : ''}
                      </div>
                    </div>
                    <div className="ar-inv-right">
                      <span className="ar-inv-bal">{currencyFormat(balance)}</span>
                      <span className={`ar-inv-flag ${overdue ? 'risk' : 'ok'}`}>{overdue ? 'PAST DUE' : 'CURRENT'}</span>
                    </div>
                  </div>
                )
              })}
              {property.collectionNotes && (
                <div className="ar-prop-notes" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(property.collectionNotes) }} />
              )}
            </div>
          )
        })}
      </div>

      <div className="ar-panel-section">
        <div className="ar-panel-label ar-panel-label-row">
          COLLECTION ACTIVITY
          {companyId !== null && (
            <button type="button" className="ar-note-btn" onClick={() => onLogNote(company)}>+ NOTE</button>
          )}
        </div>
        {activities === null && <div className="ar-panel-empty-line">Loading…</div>}
        {activities !== null && activities.length === 0 && <div className="ar-panel-empty-line">No collections activity yet</div>}
        {(activities || []).map(activity => (
          <div key={activity._id} className="ar-timeline-row">
            <span className={`ar-timeline-dot tone-${ACTIVITY_TONE[activity.subtype] || 'ink-4'}`} />
            <div className="ar-timeline-body">
              <div className="ar-timeline-text">{activity.notes || activity.contact_name || activity.subtype}</div>
              <div className="ar-timeline-meta">
                {formatWhen(activity.created_at)} · {activity.created_by || 'SYSTEM'} · {String(activity.subtype || '').toUpperCase()}
              </div>
            </div>
          </div>
        ))}
      </div>
    </aside>
  )
}

export default ArDetailPanel
